import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { gaTrack, getAttributionPayload } from "@/lib/analytics";
import SiteFooter from "../components/SiteFooter";
import SiftWordmark from "../components/SiftWordmark";
import FoundingSeatsBanner from "../components/FoundingSeatsBanner";
import AccountMenu from "../components/AccountMenu";
import { useBranding } from "../contexts/BrandingContext";
import { AudienceModeProvider } from "../contexts/AudienceModeContext";
import { isAuthenticated } from "../utils/fetchWithAuth";
import { isNative } from "../lib/platform";
import { homepageArm } from "../lib/experiment";

/* ------------------------------------------------------------------ */
/*  Public layout. Minimal nav: the whole job of these pages is to     */
/*  guide a visitor to the next step — create an account. So the nav   */
/*  carries only the logo and the auth actions; everything else lives  */
/*  in the footer / inside the app.                                    */
/* ------------------------------------------------------------------ */
export default function HeroLanding() {
  const branding = useBranding();
  const isCreamLabel = branding.branded;
  const loggedIn = isAuthenticated();
  const { pathname } = useLocation();

  const onTalent = pathname.startsWith("/for-talent") || pathname === "/contractors";
  // Kept: the mobile/audience CTAs below still route by audience even though
  // the nav no longer offers "Start free".
  const startFreeType = onTalent ? "individual" : "organization";

  // Ad landing pages are single-audience: hide the "For Businesses / For Talent"
  // switcher so paid traffic stays on one message.
  const isAdLandingPage = pathname === "/employers" || pathname === "/contractors";

  /* The marketplace homepage (pages/MarketHome) brings its own green header
     with search and categories, so this nav and the Founding banner stand
     down there. It is one arm of the homepage split, and homepageArm assigns
     the same sticky arm App's HomeSplit renders. Partner canvases still get
     the business page and this nav. */
  // The browse pages, a post, the category pages and the public profiles all wear the
  // marketplace's own green header (components/MarketHeader).
  const bare = pathname.replace(/\/+$/, "");
  const isBrowse = ["/browse", "/services", "/postings"].includes(bare)
    || bare.startsWith("/postings/") || bare.startsWith("/services/")
    || bare.startsWith("/c/") || bare.startsWith("/orgs/");
  // Partner portals get the marketplace pages too, in their own colours; only
  // the homepage split is Sift's alone.
  const isMarketHome = !isNative()
    && (isBrowse || (pathname === "/" && !branding.branded && homepageArm() === "market"));

  // "Start free" runs the get-started journey (dashboard teaser -> "your dashboard
  // is waiting" -> sign-up) instead of dropping people on a bare form. We skip the
  // audience chooser only when we already know which side the visitor is on:
  //   * single-audience pages:  /employers -> business, /contractors -> talent
  //   * Facebook ad traffic (our campaign is tagged utm_source=lpbc; fbclid is the
  //     fallback signal) that landed on an audience page: /employers -> business,
  //     /for-talent -> talent.
  // Everyone else self-selects in the branching chooser. Attribution is captured
  // on landing (App.jsx) and persists in localStorage, so it's readable here.
  const attr = getAttributionPayload();
  const isFbAd =
    (attr.attribution_utm_source || "").toLowerCase() === "lpbc" ||
    !!attr.attribution_fbclid;
  // For FB ad traffic, route off the page the ad originally dropped them on
  // (persisted in attribution) so it holds even after they've browsed elsewhere;
  // fall back to the current page for older sessions with no stored landing.
  const adPath = attr.attribution_landing_path || pathname;
  /* Which side is this person on, judged by the page they are reading.

     This only knew /employers and /contractors, so the two pages carrying
     almost all the traffic fell through to the audience chooser. "/" IS the
     business landing page (833 of 856 business visitors arrive there, against
     23 on /employers), and the talent page lives at /for-talent, not
     /contractors. So "Get started" asked people to pick a side they had already
     picked by being on the page. */
  const asBusiness = (p) => p === "/" || p === "/employers";
  const asTalent = (p) => p === "/contractors" || p.startsWith("/for-talent");

  let knownAudience = null;
  if (asBusiness(pathname)) knownAudience = "business";
  else if (asTalent(pathname)) knownAudience = "talent";
  else if (isFbAd && asBusiness(adPath)) knownAudience = "business";
  else if (isFbAd && asTalent(adPath)) knownAudience = "talent";

  /* Somebody pressing "Get started" has told us nothing about what they need,
     so business lands on the doors and gets asked. Typing into the hero field
     instead is the other entrance, and that one goes straight to the composer.
     Talent goes straight to the individual flow. */
  const startFreeTo =
    knownAudience === "business" ? "/get-started?a=business&c=1"
    : knownAudience === "talent" ? "/get-started?a=talent"
    : "/get-started";

  // Don't render nav until branding is loaded (prevents flash of unbranded nav)
  if (!branding.loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg)", color: "var(--ink)" }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: "var(--brand)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const onCream = isCreamLabel; // cream-label canvases are the partner's (often dark) accent
  const pageBg = isCreamLabel ? branding.accent_color : "var(--bg)";
  const navBg = isCreamLabel ? branding.accent_color : "rgba(250,248,242,0.85)";

  const textLink = { color: onCream ? "rgba(255,255,255,.85)" : "var(--text-secondary)", fontWeight: 500, whiteSpace: "nowrap" };

  // Audience switcher. This used to be two underlined text links, which read as
  // two more nav items competing with the logo, sign-in and Start free, and it
  // needed a whole extra row on mobile. A segmented control is one bounded
  // object instead of two: narrower, obviously a toggle, and the selected side
  // is unmistakable. It never renders on cream-label canvases, so it doesn't
  // need the onCream variants the old tabs carried.
  const switchWrap = {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    padding: 3,
    borderRadius: "var(--r-pill)",
    background: "var(--brand-tint)",
    border: "1px solid var(--border)",
  };
  const switchOpt = (active) => ({
    padding: "6px 15px",
    borderRadius: "var(--r-pill)",
    fontSize: "0.84375rem",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    fontWeight: active ? 700 : 500,
    color: active ? "#fff" : "var(--text-secondary)",
    background: active ? "var(--brand)" : "transparent",
    boxShadow: active ? "var(--shadow-sm)" : "none",
    transition: "background .18s, color .18s",
  });
  const audienceSwitch = (
    <div style={switchWrap} aria-label="Choose your audience">
      <Link to="/" aria-current={!onTalent ? "page" : undefined} style={switchOpt(!onTalent)}>
        Businesses
      </Link>
      <Link to="/for-talent" aria-current={onTalent ? "page" : undefined} style={switchOpt(onTalent)}>
        Talent
      </Link>
    </div>
  );
  const pill = {
    background: onCream ? "#fff" : "var(--ink)",
    color: onCream ? (branding.primary_color || "#111") : "var(--on-dark)",
    padding: "11px 22px",
    borderRadius: "var(--r-pill)",
    fontWeight: 600,
    whiteSpace: "nowrap",
  };

  return (
    <AudienceModeProvider>
    <div className="min-h-screen" style={{ backgroundColor: pageBg, color: onCream ? "#fff" : "var(--ink)" }}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded focus:bg-white focus:text-black focus:outline focus:outline-2 focus:outline-black"
      >
        Skip to main content
      </a>

      {/* Minimal navigation */}
      {!isMarketHome && (
      <nav
        className="sticky top-0 z-50 pt-[env(safe-area-inset-top)]"
        style={{
          backgroundColor: navBg,
          backdropFilter: onCream ? undefined : "saturate(180%) blur(12px)",
          WebkitBackdropFilter: onCream ? undefined : "saturate(180%) blur(12px)",
          borderBottom: onCream ? "1px solid rgba(255,255,255,0.18)" : "1px solid var(--border)",
        }}
      >
        <div style={{ maxWidth: 1480, margin: "0 auto" }} className="px-6 lg:px-12">
          <div className="flex justify-between items-center h-[68px]">
            <Link to={isCreamLabel ? "/auth/login" : "/"} className="hover:opacity-80 transition-opacity">
              {isCreamLabel && branding.brand_logo_url ? (
                <img src={branding.brand_logo_url} alt={branding.brand_name} className="h-8 max-w-[180px] object-contain" />
              ) : isCreamLabel ? (
                <span style={{ color: "#fff", fontSize: "1.25rem", fontWeight: 700 }}>{branding.brand_name}</span>
              ) : (
                <SiftWordmark size={30} />
              )}
            </Link>

            {/* Audience switcher — businesses (primary) and talent each get a
                dedicated page. Hidden on partner cream-label canvases and once
                signed in. */}
            {!isCreamLabel && !loggedIn && !isAdLandingPage && (
              <div className="hidden md:block">{audienceSwitch}</div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {loggedIn ? (
                <AccountMenu size={38} />
              ) : isCreamLabel ? (
                <Link to="/auth/login" style={pill}>Sign in</Link>
              ) : (
                <>
                  {/* Sign in only, since 2026-08-21.

                      The nav was carrying an audience switch, sign-in and
                      "Start free": three decisions offered before the page had
                      said anything. Asking for the account in the top-right
                      corner asks it at the one moment nobody can answer it,
                      because they have not yet been told what is here -- and
                      the page has its own CTAs, plus a sticky bar that now
                      waits until the availability posts have been read.

                      Sign in stays because it is not an ask. It is the door
                      for somebody who already decided. */}
                  <Link to="/auth/login" style={textLink} className="hover:opacity-70 transition-opacity">Sign in</Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile: the control still can't fit beside the logo, sign-in and
              Start free at 375px, so it keeps its own row. It's one compact
              object now instead of two spread-out links, so the row reads as a
              thin strip rather than a second layer of navigation. */}
          {!isCreamLabel && !loggedIn && !isAdLandingPage && (
            <div className="md:hidden flex justify-center pb-2.5 -mt-1">{audienceSwitch}</div>
          )}
        </div>
      </nav>
      )}

      {/* Founding Organization seats counter — Sift's own promo. Hidden on
          partner cream-label canvases and on the talent page (it's an
          org/business offer, not for individuals). */}
      {!isCreamLabel && !onTalent && !isMarketHome && <FoundingSeatsBanner />}

      {/* Main Content */}
      <main id="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      {isCreamLabel ? (
        <footer className="py-6 text-center text-xs" style={{ color: "rgba(255,255,255,.55)", borderTop: "1px solid rgba(255,255,255,.1)" }}>
          Powered by{" "}
          <a href="https://siftcareers.com" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "rgba(255,255,255,.75)" }}>
            Sift Careers
          </a>
        </footer>
      ) : (
        <SiteFooter />
      )}
    </div>
    </AudienceModeProvider>
  );
}
